import { auth } from './firebase';

// Configuration for new backend
const NEW_BACKEND_URL = 'https://chest-backend-v2.vercel.app'; // Forced for testing
// const NEW_BACKEND_URL = process.env.REACT_APP_NEW_BACKEND_URL || 'https://api-new.chestmusic.com';

// Debug: Log the URL being used
console.log('🏴‍☠️ NEW_BACKEND_URL:', NEW_BACKEND_URL);

/**
 * Registers or updates user data in the new backend
 * @param {Object} firebaseUser - Firebase user object
 * @returns {Promise<Object>} Response from new backend
 */
export const registerUserInNewBackend = async (firebaseUser) => {
  try {
    // Prepare user data for new backend - matching production requirements
    const userData = {
      username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'user',
      email: firebaseUser.email,
      password: 'firebase-managed', // Since Firebase handles authentication
      firebaseUid: firebaseUser.uid,
      displayName: firebaseUser.displayName || null,
      emailVerified: firebaseUser.emailVerified,
      createdAt: firebaseUser.metadata.creationTime,
      lastSignInAt: firebaseUser.metadata.lastSignInTime,
      registeredAt: new Date().toISOString(),
      onboardingCompleted: false // Will be updated later when user completes onboarding
    };

    // Get Firebase ID token for authentication with new backend
    const idToken = await firebaseUser.getIdToken();

    // Send to new backend - using the actual endpoint from backend
    const response = await fetch(`${NEW_BACKEND_URL}/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        'X-Client-Version': '1.0.0'
      },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      // If user already exists (400 error), try to get existing user data
      if (response.status === 400) {
        console.log('User already exists in new backend, fetching existing data...');
        try {
          const existingUser = await getUserFromNewBackend(firebaseUser.uid);
          return existingUser;
        } catch (getError) {
          console.warn('Could not fetch existing user:', getError);
          throw new Error(`User exists but could not fetch data: ${response.statusText}`);
        }
      }
      throw new Error(`New backend registration failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('User registered in new backend:', result);

    return result;
  } catch (error) {
    // Don't break the login flow if new backend fails
    console.error('Error registering user in new backend:', error);
    
    // Could store in localStorage to retry later
    const failedRegistration = {
      firebaseUid: firebaseUser.uid,
      email: firebaseUser.email,
      timestamp: Date.now(),
      error: error.message
    };
    
    const failedRegistrations = JSON.parse(localStorage.getItem('failedNewBackendRegistrations') || '[]');
    failedRegistrations.push(failedRegistration);
    localStorage.setItem('failedNewBackendRegistrations', JSON.stringify(failedRegistrations));
    
    return null;
  }
};

/**
 * Gets user data from new backend by Firebase UID
 * @param {string} firebaseUid - Firebase user ID
 * @returns {Promise<Object>} User data from new backend
 */
export const getUserFromNewBackend = async (firebaseUid) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const idToken = await user.getIdToken();

    const response = await fetch(`${NEW_BACKEND_URL}/user/firebase/${firebaseUid}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'X-Client-Version': '1.0.0'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get user: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error('Error getting user from new backend:', error);
    throw error;
  }
};

/**
 * Updates onboarding completion status in new backend
 * @param {string} firebaseUid - Firebase user ID
 * @returns {Promise<Object>} Response from new backend
 */
export const updateOnboardingCompletion = async (firebaseUid) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const idToken = await user.getIdToken();

    const response = await fetch(`${NEW_BACKEND_URL}/user/${firebaseUid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        onboardingCompleted: true,
        completedAt: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`Onboarding update failed: ${response.status}`);
    }

    const result = await response.json();
    console.log('Onboarding completion updated in new backend:', result);
    
    return result;
  } catch (error) {
    console.error('Error updating onboarding completion:', error);
    return null;
  }
};

/**
 * Retry failed registrations (can be called periodically)
 */
export const retryFailedRegistrations = async () => {
  try {
    const failedRegistrations = JSON.parse(localStorage.getItem('failedNewBackendRegistrations') || '[]');
    
    if (failedRegistrations.length === 0) return;
    
    console.log(`Retrying ${failedRegistrations.length} failed registrations...`);
    
    const user = auth.currentUser;
    if (!user) return;
    
    // Try to register again
    await registerUserInNewBackend(user);
    
    // Clear failed registrations if successful
    localStorage.removeItem('failedNewBackendRegistrations');
    
  } catch (error) {
    console.error('Error retrying failed registrations:', error);
  }
};